import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import Loader from './components/Loader';
import AuthScreen from './components/auth/AuthScreen';
import AuthorizationGuard from './components/auth/AuthorizationGuard';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Teachers from './pages/Teachers';
import Staff from './pages/Staff';
import Settings from './pages/Settings';
import AdminManagement from './pages/AdminManagement';
import ScheduleManagement from './pages/ScheduleManagement';
import Attendance from './pages/Attendance';
import DeviceConfiguration from './super_admin/DeviceConfiguration';
import StudentAttendanceStream from './pages/StudentAttendanceStream';
import TenantAccess from './super_admin/TenantAccess';
import NotificationsScreen from './pages/Notifications';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return <Loader />;
    }

    if (!user) {
        // Store the attempted location for redirect after login
        sessionStorage.setItem('attemptedLocation', location.pathname);
        return <AuthScreen />;
    }

    // Clear attempted location on successful login
    sessionStorage.removeItem('attemptedLocation');

    return <Layout>{children}</Layout>;
};

const PettyCash = React.lazy(() => import('./pages/finance/PettyCash'));
const Revenue = React.lazy(() => import('./pages/finance/Revenue'));
const TransactionLedger = React.lazy(() => import('./pages/finance/TransactionLedger'));
const Fees = React.lazy(() => import('./pages/finance/Fees').then(m => ({ default: m.Fees })));
const FinanceAccounts = React.lazy(() => import('./pages/finance/Accounts'));

// Library Module
const Library = React.lazy(() => import('./pages/library/Library').then(m => ({ default: m.Library })));



const AppRoutes: React.FC = () => {
    return (
        <Router>
            <React.Suspense fallback={<Loader />}>
                <Routes>
                    {/* ... existing routes ... */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/students/*"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'principal', 'hr', 'teacher']} moduleKey="Students">
                                    <Students />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/teachers/*"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'principal', 'hr']} moduleKey="Teachers">
                                    <Teachers />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/staff/*"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'principal', 'hr']} moduleKey="Staff">
                                    <Staff />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'principal']} moduleKey="Settings">
                                    <Settings />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/tenant-access"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['super_admin']}>
                                    <TenantAccess />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/notifications"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'principal', 'teacher', 'hr', 'accountant']} moduleKey="Notifications">
                                    <NotificationsScreen />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin-management"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin']} moduleKey="Management">
                                    <AdminManagement />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/schedule"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'principal', 'hr', 'teacher', 'student', 'accountant']} moduleKey="Schedule">
                                    <ScheduleManagement />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/attendance/*"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'principal', 'hr']} moduleKey="Attendance">
                                    <Attendance />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/device-configuration"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['super_admin']}>
                                    <DeviceConfiguration />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student-attendance-stream"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'principal', 'hr']} moduleKey="Student Attendance">
                                    <StudentAttendanceStream />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    {/* Leaves Route - Adding placeholder if needed or assuming it exists, but strict task is Revenue. */}
                    <Route
                        path="/leaves"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'hr']}>
                                    {/* Add Leaves component here if it exists or was planned. Keeping it invisible for now if not created. */}
                                    <div>Leaves Page</div>
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/finance/fees"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'accountant', 'principal', 'clerk']} moduleKey="Finance">
                                    <Fees />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/finance/accounts"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'accountant']} moduleKey="Finance">
                                    <FinanceAccounts />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/finance/petty-cash"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'accountant']} moduleKey="Finance">
                                    <PettyCash />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />
                    {/* Revenue Routes */}
                    <Route
                        path="/finance/revenue"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'accountant']} moduleKey="Finance">
                                    <Revenue />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />

                    {/* Ledger Routes */}
                    <Route
                        path="/finance/ledger/transactions"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'accountant']} moduleKey="Finance">
                                    <TransactionLedger />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />




                    <Route
                        path="/library"
                        element={
                            <ProtectedRoute>
                                <AuthorizationGuard requiredRoles={['admin', 'principal', 'teacher']} moduleKey="Library">
                                    <Library />
                                </AuthorizationGuard>
                            </ProtectedRoute>
                        }
                    />


                </Routes>
            </React.Suspense >
        </Router >
    );
};

const App: React.FC = () => {
    return (
        <ToastProvider>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </ToastProvider>
    );
};

export default App;
